<template>
  <v-card elevation="2" class="ma-1">
    <v-toolbar flat color="primary" dark>
      <v-toolbar-title>Paramètres du module Répartition</v-toolbar-title>
    </v-toolbar>

    <!-- Chaque onglet configure une responsabilité précise du module. -->
    <v-tabs v-model="tab" background-color="blue lighten-5" show-arrows>
      <v-tab>Général</v-tab>
      <v-tab>Encarts</v-tab>
      <v-tab>Éléments</v-tab>
      <v-tab>Répartition</v-tab>
      <v-tab>Drops recevables</v-tab>
      <v-tab>Affichage</v-tab>
      <v-tab>Actions</v-tab>
      <v-tab>Recherches</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="pa-4">
      <!-- Réglages qui déterminent la manière dont les éléments peuvent être répartis. -->
      <v-tab-item>
        <v-alert color="blue" border="left" text dense>
          Choisissez comment un élément peut être affecté. Pour les conteneurs,
          utilisez <strong>Répartissable</strong> afin de répartir une même ligne de commande entre plusieurs conteneurs.
        </v-alert>
        <v-select
          v-model="displays.params.mode"
          :items="modes"
          label="Mode de répartition"
          outlined
          dense
          hint="Exclusif : une seule destination. Répartissable : la quantité peut être ventilée."
          persistent-hint
        />
        <v-simple-table dense class="mt-4 mb-4">
          <thead>
            <tr><th>Mode</th><th>Fonctionnement</th><th>Exemple</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Exclusif</strong></td>
              <td>Un élément ne peut être affecté qu’à un seul encart.</td>
              <td>Une personne affectée à un seul groupe.</td>
            </tr>
            <tr>
              <td><strong>Multiple</strong></td>
              <td>Un élément peut être affecté à plusieurs encarts, sans gestion de quantité.</td>
              <td>Une personne inscrite à plusieurs séances.</td>
            </tr>
            <tr>
              <td><strong>Répartissable</strong></td>
              <td>Un élément peut être ventilé entre plusieurs encarts avec une quantité pour chaque affectation.</td>
              <td>Une ligne de commande répartie entre plusieurs conteneurs.</td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-switch
          v-model="displays.params.options.showUnassigned"
          label="Utiliser la liste « À répartir » intégrée au module"
          color="primary"
          hint="Désactivez cette option lorsque les éléments proviennent d’un module Liste déplaçable externe."
          persistent-hint
        />
        <v-alert v-if="!displays.params.options.showUnassigned" type="info" text dense class="mt-3">
          La colonne « À répartir » ne sera pas affichée. Les encarts restent disponibles pour recevoir les éléments d’un module Liste déplaçable compatible.
        </v-alert>
        <v-combobox
          v-model="displays.params.options.reloadModules"
          label="IDs des autres modules à rafraîchir après une modification"
          multiple chips deletable-chips outlined dense class="mt-4"
          hint="Les modules Liste déplaçable des connexions sont déjà rafraîchis automatiquement."
          persistent-hint
        />
        <v-switch
          v-model="displays.params.options.reloadOnModalClose"
          label="Rafraîchir aussi après la fermeture d’une fiche ouverte par un autre module de la page"
          color="primary"
        />
      </v-tab-item>

      <!--
        Les encarts sont les destinations du drag-and-drop : conteneurs, cuves,
        séances, groupes, etc. QueryEditor construit leur requête de lecture.
      -->
      <v-tab-item>
        <v-alert color="blue" border="left" text dense>
          Les encarts sont les destinations qui reçoivent les éléments déplacés :
          par exemple un conteneur, une cuve ou une séance. La requête ci-dessous charge ces destinations.
        </v-alert>
        <query-editor
          title="Encarts de destination"
          :config="displays.params.cards"
          :tables="tables"
          :page="page"
          :modal-key="modalKey"
          @change="value => displays.params.cards = value"
        />
        <v-autocomplete v-model="displays.params.cards.idAlias" :items="cardAliases" label="Alias de l’identifiant de l’encart" outlined dense clearable hint="Permet au module de reconnaître chaque destination du drag-and-drop." persistent-hint />
        <v-text-field
          v-model="displays.params.cards.parentField"
          label="Colonne vers la ligne parente"
          placeholder="Exemple : cmd_cls_id"
          outlined
          dense
          clearable
          hint="Cette colonne reçoit params.id lors de la création d’un encart."
          persistent-hint
        />
        <v-switch v-model="displays.params.cards.allowCreate" label="Afficher le bouton d’ajout d’un encart" color="primary" />
        <v-text-field
          v-model="displays.params.cards.addButtonLabel"
          label="Intitulé du bouton d’ajout"
          placeholder="Ajouter un encart"
          outlined
          dense
          clearable
        />
        <v-autocomplete
          v-model="displays.params.cards.newpage"
          :items="listPages"
          label="Page de création de l’encart"
          outlined
          dense
          clearable
        />
        <v-autocomplete v-model="displays.params.cards.actions" :items="listActions" label="Actions affichées sur les encarts" multiple chips deletable-chips outlined dense clearable />
        <h3 class="mt-4 mb-2">Paramètres à la création d’un encart</h3>
        <ArrayTable
          :item="{
            otherparams: { arrayTable: [
              { type: '', key: 'key', name: 'Clé du champ ou paramètre' },
              { type: 'select', key: 'type', name: 'Type', ArrayValue: [
                { text: 'Texte', value: 'text' }, { text: 'Nombre', value: 'number' }, { text: 'Booléen', value: 'bool' },
              ] },
              { type: '', key: 'value', name: 'Valeur ou variable', description: 'Exemple : [[routerquery||IDRECHERCHE_champ]] pour reprendre une recherche.' },
            ] },
            defaultprops: { show: true, tableDraggable: false, tableDel: true, hideFooter: true, hidesearch: true, addTitle: 'Ajouter un paramètre' },
          }"
          :valField="displays.params.cards.createParams"
          :change="(item, value) => { displays.params.cards.createParams = value }"
        />
      </v-tab-item>

      <!-- Les éléments sont les cartes que l’utilisateur peut faire glisser. -->
      <v-tab-item>
        <v-alert color="blue" border="left" text dense>
          Les éléments sont les lignes disponibles dans la colonne de gauche.
          Dans Prodvin, il s’agit des lignes de commande que l’utilisateur glisse vers un conteneur.
        </v-alert>
        <query-editor
          title="Éléments déplaçables"
          :config="displays.params.subjects"
          :tables="tables"
          :page="page"
          :modal-key="modalKey"
          @change="value => displays.params.subjects = value"
        />
        <v-row dense>
          <v-col cols="12" md="6"><v-autocomplete v-model="displays.params.subjects.idAlias" :items="subjectAliases" label="Alias de l’identifiant de l’élément" outlined dense clearable /></v-col>
          <v-col cols="12" md="6"><v-autocomplete v-model="displays.params.subjects.quantityAlias" :items="subjectAliases" label="Alias de la quantité disponible" outlined dense clearable hint="Facultatif : utilisé pour calculer le restant à répartir." persistent-hint /></v-col>
        </v-row>
        <v-autocomplete v-model="displays.params.subjects.actions" :items="listActions" label="Actions affichées sur les éléments disponibles" multiple chips deletable-chips outlined dense clearable />
      </v-tab-item>

      <!--
        La table de répartition est la table pivot créée lors d’un dépôt.
        Les trois colonnes indiquent comment elle relie destination, élément et quantité.
      -->
      <v-tab-item>
        <v-alert color="blue" border="left" text dense>
          Cette table enregistre le résultat du déplacement. Chaque ligne relie un encart à un élément,
          avec éventuellement une quantité, un ordre ou un commentaire.
        </v-alert>
        <query-editor
          title="Lignes de répartition"
          :config="displays.params.allocation"
          :tables="tables"
          :page="page"
          :modal-key="modalKey"
          @change="value => displays.params.allocation = value"
        />
        <v-alert type="info" text dense>
          Associez les alias retournés par la requête à leur rôle. Leurs noms sont libres ; ces sélections indiquent au module comment interpréter chaque valeur.
        </v-alert>
        <v-row dense>
          <v-col cols="12" md="3"><v-autocomplete v-model="displays.params.allocation.idAlias" :items="allocationAliases" label="Alias ID répartition" outlined dense clearable /></v-col>
          <v-col cols="12" md="3"><v-autocomplete v-model="displays.params.allocation.cardAlias" :items="allocationAliases" label="Alias ID encart" outlined dense clearable /></v-col>
          <v-col cols="12" md="3"><v-autocomplete v-model="displays.params.allocation.subjectAlias" :items="allocationAliases" label="Alias ID élément" outlined dense clearable /></v-col>
          <v-col cols="12" md="3"><v-autocomplete v-model="displays.params.allocation.quantityAlias" :items="allocationAliases" label="Alias quantité répartie" outlined dense clearable /></v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field v-model="displays.params.allocation.cardField" label="Colonne vers l’encart" placeholder="cmd_cls_containers_id" outlined dense clearable />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="displays.params.allocation.subjectField" label="Colonne vers l’élément" placeholder="cmd_cls_lines_id" outlined dense clearable />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="displays.params.allocation.quantityField" label="Colonne quantité" placeholder="quantite" outlined dense clearable />
          </v-col>
        </v-row>
        <v-alert color="amber darken-2" border="left" text dense>
          Lorsqu’une répartition existante est déplacée vers un autre encart, le module met à jour
          <strong>uniquement la colonne vers l’encart</strong> :
          <code>{{ displays.params.allocation.cardField || 'colonne non renseignée' }}</code>.
          La ligne liée, la quantité, les palettes, l’ordre et le commentaire restent inchangés.
        </v-alert>
        <v-autocomplete
          v-model="displays.params.allocation.newpage"
          :items="listPages"
          label="Page de création d’une répartition"
          outlined dense clearable
          hint="Ouverte systématiquement lorsque l’action du dépôt est une insertion."
          persistent-hint
        />
        <v-autocomplete v-model="displays.params.allocation.actions" :items="listActions" label="Actions affichées sur les éléments répartis" multiple chips deletable-chips outlined dense clearable />
        <v-divider class="my-5" />
        <v-select
          v-model="displays.params.operations.dropAction"
          :items="dropActions"
          label="Action lors du dépôt d’un élément"
          outlined dense
        />
        <v-alert type="info" text dense>
          Les champs utilisent les clés moteur de la table ciblée. Dans la valeur, vous pouvez utiliser
          <strong>tous les alias retournés par la requête « Éléments »</strong> avec la syntaxe
          <code>[[line||nom_alias]]</code>. Par exemple, si la requête retourne
          <code>cmd_cls_lines.cmd_cls_id AS idparent</code>, utilisez <code>[[line||idparent]]</code>.
        </v-alert>
        <div v-if="subjectAliases.length" class="mb-4">
          <div class="text-caption mb-1">Variables disponibles depuis la requête « Éléments » :</div>
          <v-chip v-for="alias in subjectAliases" :key="alias.value" small outlined class="mr-2 mb-2">
            [[line||{{ alias.value }}]]
          </v-chip>
        </div>
        <v-simple-table dense class="mb-4">
          <tbody>
            <tr v-for="variable in technicalVariables" :key="variable.value">
              <td><code>{{ variable.value }}</code></td><td>{{ variable.text }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <h3 class="mb-2">Champs {{ displays.params.operations.dropAction === 'update' ? 'à modifier' : 'à insérer' }} lors du dépôt</h3>
        <operation-fields
          :value="dropOperationFields"
          :fields="operationTargetFields"
          @input="updateDropFields"
        />
        <h3 class="mt-5 mb-2">Champs à modifier lors d’un déplacement</h3>
        <operation-fields
          :value="displays.params.operations.moveFields"
          :fields="allocationFields"
          @input="value => displays.params.operations.moveFields = value"
        />
      </v-tab-item>

      <v-tab-item>
        <DropReceiverAdmin
          :value="displays.params.dropReceiver"
          :pages="listPages"
          @input="value => $set(displays.params, 'dropReceiver', value)"
        />
      </v-tab-item>

      <!--
        Chaque famille possède ses variantes constructPage. Le tableau customparams
        contient les alias de sa requête. Exemple :
        [[table||cmd_cls_containers card_title]].
      -->
      <v-tab-item>
        <v-alert type="info" text dense>
          Construisez ici le contenu visible de chaque carte. Les valeurs affichées doivent utiliser les alias
          définis dans les requêtes, par exemple
          <code>[[table||cmd_cls_containers card_title]]</code> ou
          <code>[[table||cmd_cls_lines subject_remaining]]</code>.
        </v-alert>
        <v-expansion-panels>
          <v-expansion-panel v-for="kind in displayKinds" :key="kind.value">
            <v-expansion-panel-header>{{ kind.text }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="(variant, index) in displays.params.display[kind.value]" :key="`${kind.value}-${index}`">
                <v-text-field v-model="variant.key" label="Clé de variante" outlined dense />
                <template v-if="kind.value === 'card'">
                  <v-select v-model="variant.appearance.shape" :items="shapes" label="Forme de l’encart" outlined dense />
                  <v-text-field v-model.number="variant.appearance.minHeight" label="Hauteur minimale" type="number" suffix="px" outlined dense />
                </template>

                <!-- Sélectionne éventuellement la variante selon les données de la requête. -->
                <groupConditions :conditions="variant.conditions" @recepVal="value => variant.conditions = value" />

                <!-- Constructeur visuel réellement utilisé par client.vue. -->
                <CPage
                  :pageTemplate="variant.construct"
                  :page="page"
                  :modal="false"
                  :form="false"
                  :mod="true"
                  @recepVal="value => variant.construct = value"
                />
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-tab-item>

      <v-tab-item>
        <v-alert type="info" text dense>
          Créez et modifiez ici les actions du module. Vous pourrez ensuite les attribuer aux encarts,
          aux éléments disponibles ou aux éléments répartis depuis leurs onglets respectifs.
          L’icône choisie pour l’action sera affichée directement sur l’élément concerné.
        </v-alert>
        <listActions :page="page" :module="module" :modalKey="modalKey" @listActions="Actions" />
      </v-tab-item>

      <v-tab-item>
        <v-alert type="info" text dense>
          Cochez les recherches que ce module doit écouter. Leurs critères rechargent
          les encarts, les éléments et les répartitions, en filtrant les champs connus
          de chaque requête. Les jointures nécessaires doivent être configurées dans ces requêtes.
        </v-alert>
        <SimpleTable
          :Headers="[
            { text: 'ID', value: 'id' },
            { text: 'Type', value: 'type' },
            { text: 'Nom', value: 'name' },
            { text: 'Table', value: 'table' },
            { text: 'Champs', value: 'champs' },
          ]"
          :datas="dataTableSearch"
          :selected="displays.params.searchmodule"
          keyRow="id"
          @getselected="changeSearchModule"
        />
      </v-tab-item>

    </v-tabs-items>
  </v-card>
</template>

<script>
import { admin } from '../mixins'
import QueryEditor from './queryEditor.vue'
import OperationFields from './operationFields.vue'
import DropReceiverAdmin from '../shared/DropReceiverAdmin.vue'

export default {
  name: 'RepartitionAdmin',
  components: { QueryEditor, OperationFields, DropReceiverAdmin },
  mixins: [admin],
  data() {
    return {
      // Index de l’onglet Vuetify actuellement ouvert.
      tab: 0,

      // Copie réactive du display reçue par l’éditeur générique de modules.
      // Les modifications sont sauvegardées avec le module par l’éditeur parent.
      displays: this.module.displays,

      // Listes chargées au montage pour les sélecteurs de tables et de pages.
      tables: [],
      listPages: [],
      listActions: [],
      allocationFields: [],
      subjectFields: [],
      dataTableSearch: [],

      // Modes compris par client.vue et RepartitionModule.php.
      modes: [
        { text: 'Exclusif', value: 'exclusive' },
        { text: 'Multiple', value: 'multiple' },
        { text: 'Répartissable', value: 'splittable' },
      ],
      dropActions: [
        { text: 'Ouvrir la création d’une ligne de répartition', value: 'insert' },
        { text: 'Mettre à jour l’élément déplacé', value: 'update' },
      ],
      technicalVariables: [
        { value: '[[line||card_id]]', text: 'Identifiant de l’encart de destination' },
        { value: '[[line||subject_id]]', text: 'Identifiant de l’élément déplacé' },
        { value: '[[line||allocation_id]]', text: 'Identifiant de la répartition existante lors d’un déplacement' },
        { value: '[[line||quantity]]', text: 'Quantité restante ou quantité de la répartition déplacée' },
      ],

      // Formes CSS disponibles pour les encarts de destination.
      shapes: [
        { text: 'Rectangle', value: 'rectangle' },
        { text: 'Conteneur', value: 'container' },
        { text: 'Cuve verticale', value: 'tank' },
        { text: 'Cuve horizontale', value: 'horizontal-tank' },
        { text: 'Cercle', value: 'circle' },
      ],

      // Correspondance entre les trois affichages et leurs clés dans params.display.
      displayKinds: [
        { text: 'Encart', value: 'card' },
        { text: 'Élément disponible', value: 'subject' },
        { text: 'Élément réparti', value: 'allocation' },
      ],
    }
  },
  watch: {
    'displays.params.allocation.table': {
      immediate: true,
      handler(table) { this.loadAllocationFields(table) },
    },
    'displays.params.subjects.table': {
      immediate: true,
      handler(table) { this.loadSubjectFields(table) },
    },
  },
  computed: {
    cardAliases() { return this.queryAliases(this.displays.params.cards) },
    subjectAliases() { return this.queryAliases(this.displays.params.subjects) },
    allocationAliases() { return this.queryAliases(this.displays.params.allocation) },
    operationTargetFields() {
      return this.displays.params.operations.dropAction === 'update' ? this.subjectFields : this.allocationFields
    },
    dropOperationFields() {
      return this.displays.params.operations.dropAction === 'update'
        ? this.displays.params.operations.updateFields
        : this.displays.params.operations.insertFields
    },
  },
  created() {
    if (!Array.isArray(this.displays.params.searchmodule)) {
      this.$set(this.displays.params, 'searchmodule', [])
    }
    if (!Array.isArray(this.displays.params.cards.createParams)) {
      this.$set(this.displays.params.cards, 'createParams', [])
    }
    if (!this.displays.params.options || typeof this.displays.params.options !== 'object') {
      this.$set(this.displays.params, 'options', { showUnassigned: true, reloadAfterAction: true })
    }
    if (this.displays.params.options.showUnassigned === undefined) {
      this.$set(this.displays.params.options, 'showUnassigned', true)
    }
    if (!Array.isArray(this.displays.params.options.reloadModules)) {
      this.$set(this.displays.params.options, 'reloadModules', [])
    }
    if (this.displays.params.options.reloadOnModalClose === undefined) {
      this.$set(this.displays.params.options, 'reloadOnModalClose', false)
    }
    if (!this.displays.params.operations) {
      this.$set(this.displays.params, 'operations', { dropAction: 'insert', insertFields: [], updateFields: [], moveFields: [] })
    }
    if (!this.displays.params.operations.dropAction) this.$set(this.displays.params.operations, 'dropAction', 'insert')
    if (!this.displays.params.operations.updateFields) this.$set(this.displays.params.operations, 'updateFields', [])
    if (!this.displays.params.dropReceiver || typeof this.displays.params.dropReceiver !== 'object') {
      this.$set(this.displays.params, 'dropReceiver', { enabled: false, connections: [] })
    }
    if (!Array.isArray(this.displays.params.dropReceiver.connections)) {
      this.$set(this.displays.params.dropReceiver, 'connections', [])
    }
    const actionContexts = ['cards', 'subjects', 'allocation']
    actionContexts.forEach(key => {
      if (!this.displays.params[key].actions) this.$set(this.displays.params[key], 'actions', [])
    })
  },
  methods: {
    changeSearchModule(value) {
      this.displays.params.searchmodule = value
      this.$emit('recepVal', this.displays)
    },
    Actions(actions) {
      this.listActions = (actions || []).map(action => ({ text: action.name, value: action.id }))
    },
    queryAliases(config) {
      return (config.champs || []).filter(field => field && field.as).map(field => ({ text: `${field.as} (${field.field})`, value: field.as }))
    },
    loadAllocationFields(table) {
      this.allocationFields = []
      if (!table) return
      this.getListField({ filter: { table } }).then(fields => {
        this.allocationFields = (fields || []).map(field => ({
          text: `${field.cle} (${field.field})`,
          value: field.cle,
        }))
      })
    },
    loadSubjectFields(table) {
      this.subjectFields = []
      if (!table) return
      this.getListField({ filter: { table } }).then(fields => {
        this.subjectFields = (fields || []).map(field => ({ text: `${field.cle} (${field.field})`, value: field.cle }))
      })
    },
    updateDropFields(value) {
      const key = this.displays.params.operations.dropAction === 'update' ? 'updateFields' : 'insertFields'
      this.$set(this.displays.params.operations, key, value)
    },
  },
  mounted() {
    this.API({ type: 'post', url: '/engine/module/list', params: { type: ['search', 'echarts'] } }).then(response => {
      this.dataTableSearch = (Array.isArray(response.data) ? response.data : []).map(module => ({
        id: module.id,
        type: module.type,
        name: module.name,
        table: module.table,
        champs: ((module.displays.params || {}).champs || []).join(', '),
      }))
    })
    // Tables physiques proposées aux trois constructeurs de requêtes.
    this.API({ type: 'get', url: 'engine/getlisttable' }).then(response => {
      this.tables = (response.data || []).map(table => ({ text: table, value: table }))
    })

    // PageController retourne deux entrées pour chaque page formulaire :
    // new<table> pour l’insertion et edit<table> pour la modification.
    this.API({ type: 'get', url: 'allpage' }).then(response => {
      this.listPages = (response.data || [])
        .filter(page => page.type === 'form' && page.id !== this.page)
        .map(page => ({
          text: `${page.displays.displayname} (${page.name})`,
          value: page.name,
        }))
    })
  },
}
</script>
